<template>
  <div v-if="isReadOnly" class="flex flex-col gap-x-2">
    <label v-if="label" :for="fieldName" class="">
      <span class="text-brand-black-secondary">{{ t9nLabel }}</span>
      <HintTippy v-if="hint" :hint="hint"></HintTippy>
    </label>
    <span class="flex-1 text-lg">
      {{ field.value }}
    </span>
  </div>
  <div v-else class="flex flex-col gap-y-1" ref="wrapperDomRef">
    <label class="w-full flex flex-col gap-0.5">
      <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
        <div v-if="label" :class="labelClass" class="flex w-1/2 items-center">
          <slot name="label">
            {{ label }} <span v-if="isRequired" class="text-brand-red">*</span>
          </slot>
        </div>
        <div v-tippy="popoverContent" class="w-full flex flex-col gap-y-2">
          <slot name="topAppendage"></slot>
          <div
            :class="wrapperClass"
            class="relative flex items-stretch rounded-sm border border-brand-grayblue-300 has-[:focus]:border-primary"
          >
            <div class="absolute start-0 top-0 h-full px-3 flex items-center justify-center">
              <slot name="prefix"></slot>
            </div>
            <input
              v-maska
              :data-maska="parsedMask?.mask"
              :data-maska-tokens="parsedMask?.tokens"
              :data-maska-reversed="parsedMask?.reversed"
              :name="field.name"
              :placeholder="t9nPlaceholder || t9nLabel"
              :type="inputType || 'text'"
              :class="[
                inputClass,
                { 'border-s-4  border-s-primary': isRequired },
                isDisabled ? 'cursor-not-allowed' : ''
              ]"
              :maxlength="maxLength"
              :disabled="isDisabled"
              :readonly="isReadOnly"
              class="w-full ps-3 py-0.5 outline-none read-only:bg-brand-gray-250 placeholder:text-brand-gray-600 text-base placeholder:text-xs"
              v-bind="field"
              @blur="$emit('onBlur')"
              @input="$emit('onChange', $event)"
            />
            <div class="absolute end-0 top-0 h-full px-3 flex items-center justify-center">
              <slot name="suffix"></slot>
            </div>
          </div>
          <slot name="bottomAppendage"> </slot>
        </div>
      </div>
    </label>
    <div class="flex flex-col gap-y-1 text-xs ltr:font-sans">
      <span :key="err" v-for="err in errors" class="text-brand-red">
        {{ $t(err) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { vMaska } from 'maska'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import HintTippy from '@/components/Form/HintTyppy.vue'

const i18n = useI18n()

const props = defineProps({
  label: String,
  field: Object,
  errors: Array,
  name: String,
  mask: String,
  placeholder: String,
  maxLength: Number,
  inputType: {
    type: String,
    default: () => 'text'
  },
  inputClass: {
    type: String,
    default: () => ''
  },
  wrapperClass: {
    type: String,
    default: () => ''
  },
  labelClass: String,
  direction: {
    type: String,
    default: 'flex-row md:items-center'
  },
  isRequired: {
    type: Boolean,
    default: () => true
  },
  isReadOnly: Boolean,
  isDisabled: {
    type: Boolean,
    default: false
  },
  popoverContent: [Object, String]
})

const t9nLabel = computed(() => props.label && i18n.t(props.label))
const t9nPlaceholder = computed(() => props.placeholder && i18n.t(props.placeholder))

const parsedMask = computed(() => {
  /**
   * create masks for certain rules, before applying any specific masks
   */
  if (props.rules?.some((r) => r.name == 'numberOnly')) {
    return {
      mask: '999#',
      tokens: '9:[0-9]:repeated',
      reversed: true
    }
  }

  if (!props.mask) return null

  if (props.mask == 'currency') {
    return {
      mask: '9,99#.##',
      tokens: '9:[0-9]:repeated',
      reversed: true
    }
  }

  if (props.mask == 'currencyNoDecimal') {
    return {
      mask: '9,999,999',
      tokens: '9:[0-9]:repeated',
      reversed: true
    }
  }

  if (props.mask == 'numberOnly') {
    return {
      mask: '9',
      tokens: '9:[0-9]:repeated'
    }
  }

  return {
    mask: props.mask,
    tokens: null
  }
})
</script>

<style lang="scss" scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
